import React, { useCallback, useMemo } from 'react';
import {
  Title2,
  Text,
  Badge,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMsalAuth } from '../hooks/useMsalAuth';
import { createTmdsClient } from '../services/tmds';
import ExampleViewer from '../components/ExampleViewer';

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalL,
    maxWidth: '1200px',
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  blockText: { display: 'block', marginBottom: tokens.spacingVerticalM },
  badgeRow: { marginBottom: tokens.spacingVerticalM },
  badgeSpacing: { marginLeft: tokens.spacingHorizontalXS },
});

type CapabilityMapping = {
  capabilities: Record<string, { status?: string; [k: string]: unknown }>;
  substitutions?: { token: string; variable: string }[];
  [k: string]: unknown;
};

export default function Examples() {
  const styles = useStyles();
  const { getToken } = useMsalAuth();
  const client = useMemo(() => createTmdsClient(getToken), [getToken]);
  const [searchParams, setSearchParams] = useSearchParams();

  const qpCapability = searchParams.get('capability') ?? '';
  const qpExample = searchParams.get('example') ?? '';
  const qpLanguage = searchParams.get('lang') ?? '';
  const qpModel = searchParams.get('model') ?? '';
  const qpApiPath = searchParams.get('apiPath') ?? '';
  const qpCapabilities = searchParams.get('capabilities');

  // Fetch capability mapping dynamically from GET /examples
  const mappingQuery = useQuery<CapabilityMapping>({
    queryKey: ['examples-mapping'],
    queryFn: () => client.getExamplesMapping<CapabilityMapping>(),
    staleTime: 300_000,
  });

  const allCapabilities = useMemo(() => {
    const caps = mappingQuery.data?.capabilities;
    if (!caps) return [];
    return Object.entries(caps)
      .filter(([, v]) => v.status === 'enabled')
      .map(([k]) => k)
      .sort((a, b) => a.localeCompare(b));
  }, [mappingQuery.data]);

  const capabilityOptions = useMemo(() => {
    if (qpCapabilities) {
      const allowed = qpCapabilities.split(',').map((c) => c.trim()).filter(Boolean);
      return allCapabilities.filter((c) => allowed.includes(c));
    }
    return allCapabilities;
  }, [qpCapabilities, allCapabilities]);

  // Update URL when user changes selection (deep-linkable)
  const handleSelectionChange = useCallback((sel: { capability: string; example: string; language: string }) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('capability', sel.capability);
      next.set('example', sel.example);
      next.set('lang', sel.language);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <div className={styles.page}>
      <Title2>Examples</Title2>
      <Text className={styles.blockText}>
        Retrieve and preview example assets for a model capability. Select a capability, language, and API version.
      </Text>

      {qpModel && (
        <div className={styles.badgeRow}>
          <Badge appearance="tint" color="brand">Model: {qpModel}</Badge>
          {qpApiPath && <Badge appearance="tint" color="informative" className={styles.badgeSpacing}>API: {qpApiPath}</Badge>}
        </div>
      )}

      {mappingQuery.isLoading ? (
        <Spinner size="small" label="Loading capabilities…" />
      ) : capabilityOptions.length === 0 ? (
        <Text>No capabilities available.</Text>
      ) : (
        <ExampleViewer
          capabilities={[...capabilityOptions]}
          capabilityMapping={mappingQuery.data?.capabilities ?? {}}
          defaultCapability={qpCapability || undefined}
          defaultExample={qpExample || undefined}
          defaultLanguage={qpLanguage || undefined}
          model={qpModel}
          apiPath={qpApiPath}
          getToken={getToken}
          onSelectionChange={handleSelectionChange}
        />
      )}
    </div>
  );
}
